import { Box, TextField, Typography, useTheme } from "@mui/material";
import { PhotoEvent } from 'common-models';
import { FC } from 'react';
import ReactDatePicker from 'react-datepicker';

interface EventBasicInfoFormProps {
    photoEvent: PhotoEvent;
    startDate: Date;
    endDate: Date;
    onTitleChanged: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    onDatesChanged: (dates: [Date | null, Date | null], event?: React.SyntheticEvent<any, Event>) => void;
}

const EventBasicInfoForm: FC<EventBasicInfoFormProps> = ({
    photoEvent,
    startDate,
    endDate,
    onTitleChanged,
    onDatesChanged
}) => {
    const theme = useTheme();

    return (
        <>
            <Typography variant="h3" gutterBottom>
                Basic Information
            </Typography>

            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    id='event-title'
                    label='Event Title'
                    onChange={onTitleChanged}
                    value={photoEvent.title || ''}
                    variant='outlined'
                    placeholder="Enter a descriptive title for your event"
                />
            </Box>

            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                    Event Dates
                </Typography>
                <Box sx={{
                    position: 'relative',
                    zIndex: 1000,
                    '& .react-datepicker-wrapper': {
                        display: 'block',
                        width: '100%'
                    },
                    '& .date-picker-input': {
                        width: '100%',
                        minWidth: 'unset',
                        padding: '16.5px 14px',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: theme.shape.borderRadius,
                        fontSize: theme.typography.body1.fontSize,
                        fontFamily: theme.typography.fontFamily,
                        fontWeight: theme.typography.body1.fontWeight,
                        lineHeight: theme.typography.body1.lineHeight,
                        backgroundColor: 'transparent',
                        color: theme.palette.text.primary,
                        transition: theme.transitions.create(['border-color', 'box-shadow']),
                        '&:focus': {
                            outline: 'none',
                            borderColor: theme.palette.primary.main,
                            borderWidth: '2px',
                            padding: '15.5px 13px', // Adjust padding when border becomes thicker
                        },
                        '&:hover': {
                            borderColor: theme.palette.text.primary,
                        }
                    },
                    '& .react-datepicker': {
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: theme.shape.borderRadius,
                        color: theme.palette.text.primary,
                        fontFamily: theme.typography.fontFamily,
                    },
                    '& .react-datepicker__header': {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        borderBottom: 'none',
                    },
                    '& .react-datepicker__day': {
                        color: theme.palette.text.primary,
                        '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                        }
                    },
                    '& .react-datepicker__day--selected, & .react-datepicker__day--in-selecting-range, & .react-datepicker__day--in-range': {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                    }
                }}>
                    <ReactDatePicker
                        selected={startDate}
                        onChange={onDatesChanged}
                        startDate={startDate}
                        endDate={endDate}
                        dateFormat={'yyyy-MM-dd'}
                        selectsRange={true}
                        popperClassName="date-picker-popper"
                        popperPlacement="bottom-start"
                        wrapperClassName="date-picker-wrapper"
                        className="date-picker-input"
                        shouldCloseOnSelect={false}
                        isClearable={true}
                        placeholderText="Select event date range"
                    />
                </Box>
            </Box>
        </>
    );
};

export default EventBasicInfoForm;
